import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { T, rgba } from '../../utils/adminTheme';

type Step = 'form' | 'confirm' | 'done' | 'error';

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#faf9f6',
  border: `1px solid ${T.line}`,
  borderRadius: T.r.md,
  padding: '11px 14px',
  fontFamily: T.body,
  fontSize: 15,
  color: T.text,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: T.body,
  fontSize: 12,
  fontWeight: 600,
  color: T.muted,
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

interface BookingInfo {
  id: number;
  booking_date: string;
  booking_time: string;
  status: string;
  service: { name: string } | null;
  location: { name: string } | null;
  customer: { name: string; email: string } | null;
}

const CustomerCancel: React.FC = () => {
  const [bookingId, setBookingId] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [booking, setBooking] = useState<BookingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleLookup = async () => {
    const id = parseInt(bookingId, 10);
    if (!id || !email.trim()) return;

    setLoading(true);
    setErrorMsg('');

    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, booking_date, booking_time, status, service:services(name), location:locations(name), customer:customers(name, email)')
        .eq('id', id)
        .single();

      if (error || !data) {
        setErrorMsg('Keine Buchung mit dieser ID gefunden.');
        setLoading(false);
        return;
      }

      const bk = data as unknown as BookingInfo;
      const custEmail = bk.customer?.email ?? '';

      if (custEmail.toLowerCase() !== email.trim().toLowerCase()) {
        setErrorMsg('E-Mail-Adresse stimmt nicht mit der Buchung überein.');
        setLoading(false);
        return;
      }

      if (bk.status === 'cancelled') {
        setErrorMsg('Diese Buchung wurde bereits storniert.');
        setLoading(false);
        return;
      }

      setBooking(bk);
      setStep('confirm');
    } catch {
      setErrorMsg('Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', booking.id);

      if (error) throw error;
      setStep('done');
    } catch {
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return d;
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: T.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      fontFamily: T.body,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ margin: '0 0 6px', fontFamily: T.body, fontSize: 12, color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            TraumBeauty
          </p>
          <h1 style={{ margin: 0, fontFamily: T.display, fontStyle: 'italic', fontSize: 32, fontWeight: 500, color: T.text }}>
            Buchung stornieren
          </h1>
        </div>

        <div style={{
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: T.r.lg,
          padding: '28px 24px',
        }}>

          {/* STEP: form */}
          {step === 'form' && (
            <div style={{ display: 'grid', gap: 18 }}>
              <p style={{ margin: 0, color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
                Gib deine Buchungsnummer und die E-Mail-Adresse ein, mit der du gebucht hast.
              </p>

              <div>
                <label style={labelStyle}>Buchungsnummer</label>
                <input
                  type="number"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  style={inputStyle}
                  placeholder="z.B. 42"
                  min={1}
                />
              </div>

              <div>
                <label style={labelStyle}>E-Mail-Adresse</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                  placeholder="deine@email.ch"
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                />
              </div>

              {errorMsg && (
                <div style={{
                  background: rgba('#e53935', 0.07),
                  border: `1px solid ${rgba('#e53935', 0.25)}`,
                  borderRadius: T.r.md,
                  padding: '10px 14px',
                  color: '#c62828',
                  fontSize: 13,
                }}>
                  {errorMsg}
                </div>
              )}

              <button
                onClick={handleLookup}
                disabled={!bookingId || !email.trim() || loading}
                style={{
                  padding: '12px 0',
                  background: bookingId && email.trim() && !loading ? T.accent : T.faint,
                  border: 'none',
                  borderRadius: T.r.md,
                  fontFamily: T.body,
                  fontSize: 15,
                  fontWeight: 600,
                  color: bookingId && email.trim() && !loading ? '#fff' : T.muted,
                  cursor: bookingId && email.trim() && !loading ? 'pointer' : 'not-allowed',
                  transition: 'background .15s',
                }}
              >
                {loading ? 'Suche…' : 'Buchung suchen'}
              </button>
            </div>
          )}

          {/* STEP: confirm */}
          {step === 'confirm' && booking && (
            <div style={{ display: 'grid', gap: 20 }}>
              <div>
                <p style={{ margin: '0 0 14px', color: T.muted, fontSize: 14 }}>
                  Möchtest du folgende Buchung wirklich stornieren?
                </p>
                <div style={{
                  background: T.surface2,
                  border: `1px solid ${T.line}`,
                  borderRadius: T.r.md,
                  padding: '14px 16px',
                  display: 'grid',
                  gap: 8,
                }}>
                  {[
                    ['Buchungsnummer', `#${booking.id}`],
                    ['Kunde', booking.customer?.name ?? '—'],
                    ['Behandlung', booking.service?.name ?? '—'],
                    ['Standort', booking.location?.name ?? '—'],
                    ['Datum', formatDate(booking.booking_date)],
                    ['Uhrzeit', booking.booking_time],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}>
                      <span style={{ color: T.muted }}>{k}</span>
                      <span style={{ color: T.text, fontWeight: 600, textAlign: 'right' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  style={{
                    padding: '12px 0',
                    background: '#c54a3e',
                    border: 'none',
                    borderRadius: T.r.md,
                    fontFamily: T.body,
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#fff',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Stornierung läuft…' : 'Ja, Buchung stornieren'}
                </button>
                <button
                  onClick={() => { setStep('form'); setErrorMsg(''); }}
                  disabled={loading}
                  style={{
                    padding: '11px 0',
                    background: 'transparent',
                    border: `1px solid ${T.line}`,
                    borderRadius: T.r.md,
                    fontFamily: T.body,
                    fontSize: 14,
                    color: T.muted,
                    cursor: 'pointer',
                  }}
                >
                  Zurück
                </button>
              </div>
            </div>
          )}

          {/* STEP: done */}
          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>✓</div>
              <h2 style={{ margin: '0 0 10px', fontFamily: T.display, fontStyle: 'italic', fontSize: 22, fontWeight: 500, color: T.text }}>
                Buchung storniert
              </h2>
              <p style={{ margin: '0 0 24px', color: T.muted, fontSize: 14, lineHeight: 1.6 }}>
                Deine Buchung #{booking?.id} wurde erfolgreich storniert. Du erhältst eine Bestätigung per E-Mail.
              </p>
              <button
                onClick={() => { window.location.href = '/'; }}
                style={{
                  padding: '11px 24px',
                  background: T.surface2,
                  border: `1px solid ${T.line}`,
                  borderRadius: T.r.md,
                  fontFamily: T.body,
                  fontSize: 14,
                  color: T.muted,
                  cursor: 'pointer',
                }}
              >
                Zur Startseite
              </button>
            </div>
          )}

          {/* STEP: error */}
          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>✕</div>
              <h2 style={{ margin: '0 0 10px', fontFamily: T.display, fontStyle: 'italic', fontSize: 22, fontWeight: 500, color: T.text }}>
                Fehler aufgetreten
              </h2>
              <p style={{ margin: '0 0 24px', color: T.muted, fontSize: 14, lineHeight: 1.6 }}>
                Die Stornierung konnte nicht abgeschlossen werden. Bitte kontaktiere uns direkt.
              </p>
              <button
                onClick={() => setStep('form')}
                style={{
                  padding: '11px 24px',
                  background: T.surface2,
                  border: `1px solid ${T.line}`,
                  borderRadius: T.r.md,
                  fontFamily: T.body,
                  fontSize: 14,
                  color: T.muted,
                  cursor: 'pointer',
                }}
              >
                Erneut versuchen
              </button>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: T.faint }}>
          Fragen? Schreib uns unter <span style={{ color: T.accent }}>info@traumbeauty.ch</span>
        </p>
      </div>
    </div>
  );
};

export default CustomerCancel;
